import {
  differenceInCalendarDays,
  format,
  isValid,
  parse,
  startOfMonth,
} from 'date-fns';

export const DATE_INPUT_FORMAT = 'M/d/yyyy';

/** Earliest date that date pickers allow users to select. */
export const EARLIEST_SELECTABLE_DATE = new Date(2016, 1, 1);

export const INVALID_DATE_MESSAGE = 'Enter dates as MM/DD/YYYY.';

// Month and day accept one or two digits. The year must be exactly two or four digits so that a
// partially typed year such as "202" is never read as a complete date.
const TYPED_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/;

type ParseTypedDateOptions = {
  allowTwoDigitYear?: boolean;
};

/**
 * Parse a date typed as M/D/YYYY or M/D/YY. Two-digit years resolve relative to the current year,
 * so "24" becomes 2024. Returns undefined for incomplete or impossible dates such as 2/30/2024.
 */
export const parseTypedDate = (
  text: string,
  options: ParseTypedDateOptions = {},
): Date | undefined => {
  const { allowTwoDigitYear = true } = options;
  const trimmedText = text.trim();
  const match = TYPED_DATE_PATTERN.exec(trimmedText);
  if (!match) return undefined;

  const isTwoDigitYear = match[3].length === 2;
  if (isTwoDigitYear && !allowTwoDigitYear) return undefined;

  const date = parse(
    trimmedText,
    isTwoDigitYear ? 'M/d/yy' : DATE_INPUT_FORMAT,
    new Date(),
  );
  return isValid(date) ? date : undefined;
};

export const formatInputDate = (date: Date): string => format(date, DATE_INPUT_FORMAT);

export const isWithinDateBounds = (date: Date, minDate: Date, maxDate: Date): boolean =>
  differenceInCalendarDays(date, minDate) >= 0 &&
  differenceInCalendarDays(maxDate, date) >= 0;

export const getOutOfBoundsMessage = (minDate: Date, maxDate: Date): string =>
  `Choose a date between ${formatInputDate(minDate)} and ${formatInputDate(maxDate)}.`;

/**
 * Resolve a typed date once editing ends. Empty text clears the date; invalid or out-of-bounds
 * text produces an error message instead of a date.
 */
export const resolveTypedDate = (
  text: string,
  minDate: Date,
  maxDate: Date,
): { date?: Date; error?: string } => {
  if (!text.trim()) return {};

  const date = parseTypedDate(text);
  if (!date) return { error: INVALID_DATE_MESSAGE };
  if (!isWithinDateBounds(date, minDate, maxDate)) {
    return { error: getOutOfBoundsMessage(minDate, maxDate) };
  }
  return { date };
};

/**
 * Return the first day of the month to display for `date`, kept within the selectable bounds so
 * the calendar never opens on a month in which every day is disabled.
 */
export const clampMonth = (date: Date, minDate: Date, maxDate: Date): Date => {
  if (differenceInCalendarDays(date, minDate) < 0) return startOfMonth(minDate);
  if (differenceInCalendarDays(maxDate, date) < 0) return startOfMonth(maxDate);
  return startOfMonth(date);
};
